import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { X, Check, Loader2, ArrowRight, KeyRound, Timer } from "lucide-react";

/** 48-hour concierge checklist for one merchant. Auto steps derive from data; QR kit + approval are ticked by the team. */
export function SetupWizardModal({ org, onClose, onDesign, onActivate }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!org) return;
    setData(null);
    api.get(`/admin/merchants/${org.id}/setup`).then((r) => { setData(r.data); setNotes(r.data.notes || ""); }).catch((e) => toast.error(apiError(e)));
  }, [org?.id]);

  if (!org) return null;

  const toggle = async (step, done) => {
    setBusy(step);
    try {
      const { data: d } = await api.post(`/admin/merchants/${org.id}/setup/${step}`, { done });
      setData(d);
      toast.success(done ? "Marked done" : "Reopened");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const saveNotes = async () => {
    setBusy("notes");
    try { const { data: d } = await api.post(`/admin/merchants/${org.id}/setup/notes`, { notes }); setData(d); toast.success("Notes saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const isProspect = org.pipeline_status === "prospect";
  const actionFor = (s) => {
    if (s.done) return null;
    if (["details", "logo", "program", "live"].includes(s.key)) return { label: s.key === "live" ? "Publish" : "Open editor", onClick: () => onDesign(org), icon: ArrowRight };
    if (s.key === "approval" && isProspect) return { label: "Activate", onClick: () => onActivate(org), icon: KeyRound };
    return null;
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#0B0D10]/60 backdrop-blur-sm"
        onClick={onClose} data-testid="setup-wizard-modal">
        <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[600px] max-h-[92vh] overflow-y-auto bg-[#F4F1E9] rounded-[24px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.55)]">
          <div className="bg-[#0B0D10] text-white px-6 py-5 flex items-center justify-between sticky top-0 z-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#C8FF4D]">48-hour setup</div>
              <div className="font-display font-extrabold text-[19px] tracking-tight mt-0.5">{org.name}</div>
            </div>
            <div className="flex items-center gap-3">
              {data && <SlaChip hoursLeft={data.hours_left} complete={data.complete} />}
              <button data-testid="setup-wizard-close" onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center bg-white/[0.08] hover:bg-white/[0.15]"><X size={16} /></button>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {!data ? <div className="text-[13px] text-[#3a3d44]/60 animate-pulse">Loading checklist…</div> : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex-1 h-2 rounded-full bg-[#E4E0D6] overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(data.done / data.total) * 100}%` }} transition={{ duration: 0.5 }} className="h-full bg-[#C8FF4D]" />
                  </div>
                  <div className="font-display font-extrabold text-[15px] tabular-nums" data-testid="setup-progress">{data.done}/{data.total}</div>
                </div>
                <ol className="space-y-2.5" data-testid="setup-steps">
                  {data.steps.map((s, i) => {
                    const act = actionFor(s);
                    return (
                      <li key={s.key} data-testid={`setup-step-${s.key}`} className={`flex items-center gap-3.5 rounded-2xl border p-3.5 ${s.done ? "bg-white border-[#E8E5DE]" : "bg-[#FBF9F4] border-dashed border-[#DAD5C8]"}`}>
                        <div className={`h-8 w-8 rounded-full grid place-items-center shrink-0 font-display font-extrabold text-[12px] ${s.done ? "bg-[#C8FF4D] text-[#0B0D10]" : "bg-[#0B0D10] text-white"}`}>
                          {s.done ? <Check size={15} strokeWidth={3} /> : i + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`font-semibold text-[14px] ${s.done ? "" : "text-[#0B0D10]"}`}>{s.label}</div>
                          <div className="text-[12px] text-[#3a3d44]/60 truncate">{s.hint}</div>
                        </div>
                        {!s.auto ? (
                          <button data-testid={`setup-toggle-${s.key}`} disabled={busy === s.key} onClick={() => toggle(s.key, !s.done)}
                            className={`btn-lift rounded-full h-9 px-3.5 text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 ${s.done ? "border border-[#DAD5C8] text-[#3a3d44] hover:text-[#0B0D10]" : "bg-[#C8FF4D] text-[#0B0D10]"}`}>
                            {busy === s.key ? <Loader2 size={13} className="animate-spin" /> : (s.done ? "Undo" : "Mark done")}
                          </button>
                        ) : act ? (
                          <button data-testid={`setup-action-${s.key}`} onClick={act.onClick}
                            className="btn-lift rounded-full h-9 px-3.5 text-[12.5px] font-semibold inline-flex items-center gap-1.5 bg-[#0B0D10] text-white">
                            {act.label} <act.icon size={12} />
                          </button>
                        ) : <span className="text-[10.5px] uppercase tracking-[0.12em] text-[#3a3d44]/45 pr-1">auto</span>}
                      </li>
                    );
                  })}
                </ol>
                <div className="mt-6">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium mb-1.5">Handover notes</div>
                  <textarea data-testid="setup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Colours they asked for, who to call, when the poster ships…"
                    className="w-full rounded-2xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] focus:outline-none px-3.5 py-2.5 text-[13.5px]" />
                  <button data-testid="setup-notes-save" disabled={busy === "notes" || notes === (data.notes || "")} onClick={saveNotes}
                    className="mt-2 text-[12.5px] font-semibold text-[#0B0D10] disabled:opacity-40 inline-flex items-center gap-1.5">
                    {busy === "notes" ? <Loader2 size={12} className="animate-spin" /> : null}Save notes
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function SlaChip({ hoursLeft, complete, compact = false, onLight = false }) {
  if (complete) return <span data-testid="setup-sla" className={`inline-flex items-center gap-1 rounded-full bg-[#C8FF4D] text-[#0B0D10] font-bold ${compact ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"}`}><Check size={10} strokeWidth={3} />Live</span>;
  if (hoursLeft == null) return null;
  const overdue = hoursLeft < 0;
  const label = overdue ? `${Math.abs(Math.round(hoursLeft))}h over` : `${Math.round(hoursLeft)}h left`;
  const calm = onLight ? "bg-[#EFEBE1] text-[#3a3d44]" : "bg-white/[0.1] text-white";
  const tone = overdue ? "bg-[#FF7A3D]/15 text-[#c24e16]" : hoursLeft < 12 ? "bg-amber-100 text-amber-900" : calm;
  return <span data-testid="setup-sla" className={`inline-flex items-center gap-1 rounded-full font-semibold tabular-nums ${tone} ${compact ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"}`}><Timer size={10} />{label}</span>;
}
